import React from "react";
import { boardRowProps } from "./boardDrag.js";
import { C } from "../lib/colors.js";
import { matchesTitle, searchWords } from "../lib/forecast/titleSearch.js";
import { splitForecastLists, mergeByReceivedAt } from "../lib/forecast/forecastLists.js";
import { orderForecasts } from "../lib/forecast/orderForecasts.js";
import { STACK_NAME_MAX, layoutWithStacks } from "../lib/forecast/stacks.js";
import { isReadOnlyRow } from "../lib/forecast/sharedRowGuards.js";
import { surface, ui } from "../lib/telemetry/instrument.js";
import { toast } from "../lib/ui/toast.js";
import { Button } from "./ui/Primitives.jsx";
import { IconForecasts } from "./ui/Icons.jsx";
import { SharedLaneTail } from "./SharedLaneTail.jsx";
import { WatchlistRow } from "./WatchlistRow.jsx";
import { RecentRow } from "./RecentRow.jsx";

// Resting card outline. Deliberately NOT `C.border` — that token is the faint hairline used
// for dividers INSIDE a card, and at the card's own edge it left the cards barely separated
// from the panel behind them. A touch of the mid grey lifts the edge without competing with
// the accent border a watched or active card carries.
// Softer than the old #4d5566: at that contrast every card in the list drew its own box,
// which competed with the one signal the outline carries — WHICH card is selected. The
// selected card keeps the purple edge (rowSelectedEdge), so the two states now differ in
// weight as well as hue.
const CARD_BORDER = "#3d4350";

// ── THE CARD ──────────────────────────────────────────────────────────────────
//
// One card for both sections: the question (two lines), a quiet meta line ("Updated 2d ago ·
// Resolves Jan 2029"), the answer on the right, and a visible "More" menu that holds every
// action (open, watchlist, share, tracking settings, delete) — so none of them hides behind
// another. Watchlist membership is the section the card sits in; it changes nothing else.

// The card's styles need hover, focus and :has(), so they are real CSS, injected once by the
// list. `forecast-log-card` stays on the root for the stack layout and drag code.
const CARD_CSS = `
.forecast-log-card.sy-fcard{display:grid;grid-template-columns:minmax(0,1fr) auto;grid-template-areas:"title menu" "foot foot";align-items:start;column-gap:12px;padding:10.2px 12px 8.5px 14px;border-radius:12px;overflow:visible;background:var(--c-rowInactive);border:1px solid var(--c-border);transition:border-color .12s ease,background .12s ease;}
.forecast-log-card.sy-fcard:hover{border-color:var(--c-purpleDim);}
.forecast-log-card.sy-fcard[data-selected]{background:var(--c-rowActive);border-color:color-mix(in srgb,var(--c-accent) 55%,transparent);}
.forecast-log-card.sy-fcard:has(.sy-menu){z-index:30;}
.sy-fcard__title{grid-area:title;font-size:14px;font-weight:500;line-height:20px;min-height:40px;color:var(--c-white);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:anywhere;}
.sy-fcard__foot{grid-area:foot;margin-top:8px;display:flex;align-items:center;column-gap:12px;min-width:0;}
.sy-fcard__meta{display:flex;align-items:center;gap:12px;min-width:0;flex:0 1 auto;font-size:12px;line-height:16px;color:var(--c-gray);}
.sy-fcard__meta-text{flex-shrink:0;white-space:nowrap;}
.sy-fcard__moved{display:inline-flex;align-items:center;gap:4px;color:var(--c-accent);min-width:0;overflow:hidden;white-space:nowrap;}
.sy-fcard__moved svg,.sy-fcard__cadence svg{flex-shrink:0;}
.sy-fcard__moved svg{width:12px;height:12px;}
.sy-fcard .collab-badge{position:relative!important;top:auto!important;right:auto!important;flex-shrink:0;}
.sy-fcard .collab-badge svg{width:20px;height:15px;}
.sy-fcard .collab-badge{transform:translateY(-1px);}
.sy-fcard__cadence{display:inline-flex;align-items:center;gap:5px;min-width:0;overflow:hidden;white-space:nowrap;}
.forecast-log-card.sy-fcard[data-picked]{border-color:var(--c-accent);box-shadow:0 0 0 1px var(--c-accent);}
.sy-fcard__pick{position:absolute;top:-7px;left:-7px;width:18px;height:18px;border-radius:9px;background:var(--c-accent);color:var(--c-btnText);display:flex;align-items:center;justify-content:center;z-index:4;}
.sy-fcard__pick svg{width:12px;height:12px;}
.sy-fpicked{position:sticky;top:-12px;z-index:35;display:flex;align-items:center;gap:10px;margin:-12px -12px 10px;padding:10px 12px 10px 14px;background:var(--c-panel);border-bottom:1px solid var(--c-edge);font-size:13px;color:var(--c-white);}
.sy-fpicked__hint{flex:1;min-width:0;font-size:12px;color:var(--c-gray);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.sy-fcard__answer{flex:1 1 0;min-width:0;display:flex;align-items:baseline;justify-content:flex-end;gap:8px;text-align:right;}
.sy-fcard__label{min-width:0;font-size:13px;line-height:18px;color:var(--c-grayL);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.sy-fcard__pct{flex-shrink:0;font-size:18px;font-weight:600;line-height:22px;color:var(--c-white);}
.sy-fcard__state{display:inline-flex;align-items:center;gap:6px;min-width:0;font-size:13px;line-height:18px;font-weight:500;}
.sy-fcard__state svg{width:14px;height:14px;}
.sy-fcard__menu{grid-area:menu;position:relative;justify-self:end;align-self:start;margin:-4px -7px 0 4px;}
.sy-fcard__menu .sy-iconbtn{width:28px;height:28px;padding:0;color:var(--c-gray);}
.sy-fcard__menu .sy-iconbtn[aria-expanded="true"]{background:var(--c-rowActive);color:var(--c-white);}
.sy-menu{position:absolute;right:0;z-index:40;min-width:212px;padding:4px;background:var(--c-tooltipBg);border:1px solid var(--c-border);border-radius:8px;box-shadow:var(--c-shadowBox);cursor:default;}
.sy-menu button{display:flex;align-items:center;gap:10px;width:100%;height:32px;padding:0 10px;border:0;border-radius:6px;background:transparent;color:var(--c-white);font:inherit;font-size:13px;text-align:left;cursor:pointer;white-space:nowrap;}
.sy-menu button svg{width:16px;height:16px;color:var(--c-gray);flex-shrink:0;}
.sy-menu button:hover,.sy-menu button:focus-visible{background:var(--c-rowActive);outline:none;}
.sy-menu button[data-danger]{color:var(--c-danger);}
.sy-menu button[data-danger] svg{color:var(--c-danger);}
.sy-menu hr{border:0;height:1px;background:var(--c-border);margin:4px 0;}
.sy-fsection{display:flex;align-items:center;gap:10px;padding:22px 2px 12px;}
.sy-fsection[data-first]{padding-top:4px;}
.sy-fsection::before,.sy-fsection::after{content:"";flex:1;height:1px;background:var(--c-edge);}
.sy-fsection h3{margin:0;font-size:13px;font-weight:600;color:var(--c-white);letter-spacing:0;}
.sy-fsection span{font-size:11px;line-height:18px;letter-spacing:.06em;color:var(--c-grayL);}
.sy-fsection span::before{content:"[";}
.sy-fsection span::after{content:"]";}
`;

/** A collapsed stack shows at most this many cards: its lead and the edges of two behind. */
const STACK_EDGES_MAX = 2;

/** How far each card behind sits down and to the right of the one in front. */
const STACK_EDGE_DY = 6;

const STACK_EDGE_DX = 5;

/**
 * The tab's curves, and how far in it sits. Its left curve reaches STACK_TAB_R + 1 past the
 * tab, and must land on the STRAIGHT top edge, clear of the rounded corner of the card (14)
 * and of the open bundle (16): 16 + 9, plus a short straight run between the two curves.
 */
const STACK_TAB_R = 8;

const STACK_TAB_INSET = 28;

/**
 * One inside curve at the base of a stack's tab: an arc from the edge line below up into the
 * tab's side, drawn in the border color, with the flare under it filled in the tab's color so
 * the edge line and the tab's own side are hidden beneath it. Sits just outside the tab, on
 * the 1px line the tab overlaps (whole and half pixels, so the strokes land crisp).
 */
function TabFlare({ side, bg, line }) {
  const W = STACK_TAB_R + 1;
  const H = STACK_TAB_R + 1;
  const x0 = W - 0.5 - STACK_TAB_R;
  const arc = `M ${x0} ${H - 0.5} A ${STACK_TAB_R} ${STACK_TAB_R} 0 0 0 ${W - 0.5} ${H - 0.5 - STACK_TAB_R}`;
  // The fill runs past the box on the right and below, over the tab's side border and
  // the line, so no partial-pixel sliver of either shows through under the flare.
  const fill = `M ${x0} ${H + 2} L ${x0} ${H - 0.5} A ${STACK_TAB_R} ${STACK_TAB_R} 0 0 0 ${W - 0.5} ${H - 0.5 - STACK_TAB_R} L ${W + 1} ${H - 0.5 - STACK_TAB_R} L ${W + 1} ${H + 2} Z`;
  return (
    <svg
      aria-hidden="true"
      width={W}
      height={H}
      style={{
        position: "absolute",
        // On the line: the tab overlaps the edge by 2px, its 1px line sitting 1px up.
        bottom: 1,
        // The tab's 1px side border sits just outside its padding box, which this is placed in.
        [side]: -W,
        transform: side === "right" ? "scaleX(-1)" : undefined,
        overflow: "visible",
        pointerEvents: "none",
      }}
    >
      <path d={fill} fill={bg} />
      <path d={arc} fill="none" stroke={line} strokeWidth="1" />
    </svg>
  );
}

/** A stack of cards: the mark on a stack's tab, so it reads as a group, not a section label. */
function StackGlyph() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block", flexShrink: 0 }}
    >
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

/**
 * A stack of forecast cards (the rail layout's decision bundles). Its name sits on a folder
 * tab joined to the top edge of the cards it names — the lead card when collapsed (the cards
 * behind peeking out below and to the right like a small pile), the bundle's border when open.
 * Clicking the tab row opens or closes it. Cards move in and out of it like any other card,
 * and the whole stack drags as one item in its section.
 */
function StackGroup({ item, section, stacking, handlers, renderRow, leadActive }) {
  const { stack, lead, cards } = item;
  const open = stacking.expanded.has(stack.id);
  const editing = stacking.editingId === stack.id;
  const toggle = () => stacking.onToggle(stack.id);
  // The tab shares the surface it joins: the lead card's fill when collapsed (its selected
  // fill while it is the active forecast), the panel behind the bundle when open.
  const tabBg = open ? C.panel : leadActive ? C.rowActive : C.rowInactive;
  const tabBorder = open ? C.purpleDim : CARD_BORDER;
  // The whole row opens/closes the stack (tab, and the space beside it), except while the
  // name is being edited.
  const header = (
    <div
      onClick={editing ? undefined : toggle}
      title={editing ? undefined : open ? "Collapse stack" : "Open stack"}
      // Keyboard-operable like a button: a collapsed stack's cards are only reachable through it.
      role={editing ? undefined : "button"}
      // ...and still the handle the whole stack is dragged by (boardDrag.js).
      data-drag-handle=""
      {...ui("forecast-manager.stack-toggle", stack.id)}
      tabIndex={editing ? undefined : 0}
      aria-expanded={editing ? undefined : open}
      aria-label={editing ? undefined : `${stack.name}, stack of ${cards.length}`}
      onKeyDown={
        editing
          ? undefined
          : (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggle();
              }
            }
      }
      style={{
        display: "flex",
        alignItems: "flex-end",
        cursor: editing ? "default" : "pointer",
        // Overlaps the edge below by 2px — its 1px border line plus a pixel of margin, since the
        // root's CSS zoom puts edges on fractional pixels and an exact overlap leaves a sliver.
        marginBottom: -2,
        position: "relative",
        zIndex: 2,
      }}
    >
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 5,
          marginLeft: STACK_TAB_INSET,
          maxWidth: `calc(100% - ${STACK_TAB_INSET * 2}px)`,
          padding: "2px 9px 4px 8px",
          background: tabBg,
          // Three sides as longhands, never `border` + `borderBottom: none`: when the color
          // changes (opening the stack), React re-applies a `border` shorthand and the bottom
          // edge would come back as a line across the join.
          borderTop: `1px solid ${tabBorder}`,
          borderLeft: `1px solid ${tabBorder}`,
          borderRight: `1px solid ${tabBorder}`,
          borderRadius: `${STACK_TAB_R + 2}px ${STACK_TAB_R + 2}px 0 0`,
          fontSize: 11,
          color: C.accent,
          minWidth: 0,
        }}
      >
        {/* The base flares out into the edge below in two inside curves, so the tab and the
            card read as one outline (a browser tab's join). */}
        <TabFlare side="left" bg={tabBg} line={tabBorder} />
        <TabFlare side="right" bg={tabBg} line={tabBorder} />
        <StackGlyph />
        {editing ? (
          <input
            // Remounts when a suggested name arrives, so the field shows it (still selected).
            key={stack.name}
            autoFocus
            defaultValue={stack.name}
            maxLength={STACK_NAME_MAX}
            aria-label="Stack name"
            {...ui("forecast-manager.stack-name", stack.id)}
            onChange={() => stacking.onNameTyped(stack.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter") stacking.onRename(stack.id, e.currentTarget.value);
              if (e.key === "Escape") stacking.onCancelRename();
            }}
            onBlur={(e) => stacking.onRename(stack.id, e.currentTarget.value)}
            onFocus={(e) => e.currentTarget.select()}
            style={{
              minWidth: 0,
              width: 160,
              background: C.rowInactive,
              border: `1px solid ${C.purpleDim}`,
              borderRadius: 6,
              color: C.white,
              font: "inherit",
              fontWeight: "bold",
              padding: "1px 6px",
            }}
          />
        ) : (
          <span
            data-stack-name=""
            style={{
              fontWeight: "bold",
              letterSpacing: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {stack.name}
          </span>
        )}
        <span style={{ color: C.gray, flexShrink: 0 }}>· {cards.length}</span>
        {open && !editing && (
          <button
            type="button"
            aria-label="Rename stack"
            title="Rename stack"
            {...ui("forecast-manager.stack-rename", stack.id)}
            onClick={(e) => {
              e.stopPropagation();
              stacking.onStartRename(stack.id);
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              marginLeft: 2,
              cursor: "pointer",
              color: C.gray,
              lineHeight: 0,
              flexShrink: 0,
            }}
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
            </svg>
          </button>
        )}
      </div>
      <div style={{ flex: 1, alignSelf: "stretch" }} />
    </div>
  );
  const drag = boardRowProps(lead.id, section, handlers, { stackId: stack.id });
  if (!open) {
    const edges = Math.min(cards.length - 1, STACK_EDGES_MAX);
    return (
      <div
        data-stack-group=""
        {...surface("forecast-manager.stack", stack.id)}
        className="forecast-stack"
        {...drag}
        style={{ position: "relative", paddingBottom: edges * STACK_EDGE_DY }}
      >
        {header}
        <div style={{ position: "relative", marginRight: edges * STACK_EDGE_DX }}>
          {/* Furthest first, so each nearer card paints over the one behind it. */}
          {Array.from({ length: edges }, (_, i) => edges - i).map((d) => (
            <div
              key={d}
              aria-hidden="true"
              style={{
                position: "absolute",
                top: d * STACK_EDGE_DY,
                bottom: -d * STACK_EDGE_DY,
                left: d * STACK_EDGE_DX,
                right: -d * STACK_EDGE_DX,
                background: C.rowInactive,
                border: `1px solid ${CARD_BORDER}`,
                borderRadius: 14,
                opacity: d === 1 ? 1 : 0.6,
              }}
            />
          ))}
          {renderRow(lead, { onCardClick: toggle, dragProps: {} })}
        </div>
      </div>
    );
  }
  const list = `stack:${stack.id}`;
  return (
    <div
      data-stack-group=""
      {...surface("forecast-manager.stack", stack.id)}
      data-board-stack-area={list}
      className="forecast-stack forecast-stack--open"
      {...drag}
    >
      {header}
      <div
        style={{
          border: `1px solid ${C.purpleDim}`,
          borderRadius: 16,
          padding: "8px 6px 0",
        }}
      >
        {cards.map((f) => renderRow(f, { dragProps: boardRowProps(f.id, list, handlers) }))}
      </div>
    </div>
  );
}

// Two-section Watchlist content. Pinned forecasts → Watchlist section
// with full meta lines; unpinned → Recent section with slim rows. The
// section headers + helper text come from the handoff verbatim.
export function WatchlistLogContent({
  forecasts,
  isWatchlisted,
  active,
  onSelect,
  onTogglePin,
  onReorder,
  onOpenSettings,
  getTrackingPrefs,
  compareLaneOf,
  compareMode,
  debugUI,
  // Debug "Force Resolution" status (resolved_yes / resolved_no / unresolved).
  // Applied to the ACTIVE forecast's card only, so the resolved-card treatment is
  // previewable without a real data-plane resolution.
  debugResolutionOverride = null,
  // "Now" (effectiveToday) for the unresolved-past-date check.
  nowMs,
  // Task 2.5 — the shared-with-me feed + its load lifecycle (useSharedForecasts's
  // return value, threaded straight through). All optional, so a caller predating
  // the shared lane needs no change: an omitted `sharedForecasts` defaults to an
  // empty array and the merge below is then the identity.
  sharedForecasts = [],
  // The manual order, re-applied after the shared merge (which sorts by received time).
  forecastOrder = [],
  sharedLoading = false,
  sharedHasMore = false,
  sharedError = false,
  onLoadMoreShared,
  // Threaded to the rows so the corner badge can open the share dialog.
  onOpenShare,
  // Forecast stacks (the rail layout; useForecastStacks's return value). Absent → no stacks,
  // and the list renders exactly as it always has.
  stacking = null,
  // Place a forecast in the saved order: (id, beforeId, afterId). The board's drops use it.
  onMoveInOrder,
  // The watchlist's capacity, and what to call when a move would exceed it.
  watchlistLimit = Infinity,
  onWatchlistFull,
  // Opens the delete confirmation for a card (its menu's "Delete forecast").
  onDelete,
  // What is typed in the panel's search; blank shows everything.
  filterQuery = "",
}) {
  // compareLaneOf(f) → 0/1 for a selected compare lane, else -1. Without it the
  // log shows no lane-color border, so a 2-way compare selection has no visual
  // indicator.
  const laneOf = compareLaneOf || (() => -1);
  // Cards picked for a batch action (shift- or Cmd-click). Escape drops them.
  const [picked, setPicked] = React.useState(() => new Set());
  React.useEffect(() => {
    if (!picked.size) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPicked(new Set());
    };
    // telemetry-gesture: Escape drops a batch selection (dismissing)
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [picked]);
  // Shared split — see lib/forecast/forecastLists.js. `isWatchlisted` keeps a
  // RESOLVED contract in its watchlist spot even though the server drops terminal
  // contracts from `pinned` (isTracked → false), so the user sees the OUTCOME
  // (OCCURRED / DID NOT OCCUR, rendered by the row badge) instead of it silently
  // sliding into Recent. Machine-minted trigger questions never list here (#2598)
  // — that filter lives in the shared split too, so both variants agree.
  //
  // Neither section is windowed: FORECAST_DISPLAY_CAP upstream is the single
  // bound on how much reaches here. Watchlist rows older than that cap are merged
  // into `forecasts` in AppInner, so tracking a forecast surfaces it here
  // regardless of its age — subject to the isUserAsked filter above, which keeps
  // minted rows out even when tracked.
  // Owned ∪ shared, ordered by when the viewer RECEIVED each and then by the manual
  // drag order, before the watchlist/recent split — so a shared forecast lands in the same two sections
  // as everything else rather than in a third one below them.
  const { watchlist: pins, recent } = splitForecastLists(
    orderForecasts(mergeByReceivedAt(forecasts, sharedForecasts), forecastOrder),
    isWatchlisted,
  );
  const watchRow = (f, extra = {}) => (
    <WatchlistRow
      key={f.id}
      f={f}
      isActive={active?.id === f.id}
      compareLane={laneOf(f)}
      compareMode={compareMode}
      prefs={getTrackingPrefs(f.id)}
      onSelect={onSelect}
      onToggleWatch={(fc) => onTogglePin(fc.id)}
      onReorder={onReorder}
      onOpenSettings={onOpenSettings}
      onOpenShare={onOpenShare}
      onDelete={onDelete}
      debugUI={debugUI}
      nowMs={nowMs}
      picked={picked.has(f.id)}
      onPick={onPick}
      batch={picked.has(f.id) ? batch : null}
      resolutionOverride={
        debugResolutionOverride && f.id === active?.id ? debugResolutionOverride : null
      }
      {...extra}
    />
  );
  // A shared row reorders and stacks like any other: order and stacks are the viewer's own
  // preferences (users.preferences), never written to the owner's forecast.
  const recentRow = (f, extra = {}) => {
    return (
      <RecentRow
        key={f.id}
        f={f}
        isActive={active?.id === f.id}
        compareLane={laneOf(f)}
        compareMode={compareMode}
        nowMs={nowMs}
        prefs={getTrackingPrefs(f.id)}
        picked={picked.has(f.id)}
        onPick={onPick}
        batch={picked.has(f.id) ? batch : null}
        resolutionOverride={
          debugResolutionOverride && f.id === active?.id ? debugResolutionOverride : null
        }
        onSelect={onSelect}
        // Offered to a recipient too — watchlist membership is per-user.
        onToggleWatch={(fc) => onTogglePin(fc.id)}
        onReorder={onReorder}
        onOpenShare={onOpenShare}
        onOpenSettings={onOpenSettings}
        onDelete={onDelete}
        debugUI={debugUI}
        {...extra}
      />
    );
  };
  // The panel's search narrows both sections to forecasts whose titles hold every typed word.
  // While it does, the matches show as plain cards: no stacks, and nothing moves on the board.
  const words = searchWords(filterQuery);
  const searching = words.length > 0;
  const pinsShown = searching ? pins.filter((f) => matchesTitle(f, words)) : pins;
  const recentShown = searching ? recent.filter((f) => matchesTitle(f, words)) : recent;
  // With stacking on, each section is cards and stacks; a card renders as it always has.
  const sections =
    stacking && !searching
      ? layoutWithStacks({ watchlist: pins, recent }, stacking.stacks)
      : {
          watchlist: pinsShown.map((f) => ({ kind: "card", f })),
          recent: recentShown.map((f) => ({ kind: "card", f })),
        };
  // The board: a card or a whole stack moves between Watchlist, Recent and open stacks, and
  // takes on where it lands — its watch state follows its section, a stacked card's follows
  // its stack's.
  const byId = new Map([...pins, ...recent].map((f) => [f.id, f]));
  // The open forecast already reads as selected, so the first shift-click adds to it.
  const onPick = (f) => {
    if (!f) {
      if (picked.size) setPicked(new Set());
      return;
    }
    setPicked((prev) => {
      const next = new Set(prev);
      if (!next.size && active?.id && active.id !== f.id && byId.has(active.id))
        next.add(active.id);
      if (next.has(f.id)) next.delete(f.id);
      else next.add(f.id);
      return next;
    });
  };
  const pickedRows = [...picked].map((id) => byId.get(id)).filter(Boolean);
  const clearPicked = () => setPicked(new Set());
  const batch =
    pickedRows.length > 1
      ? {
          rows: pickedRows,
          isWatched: (r) => !!isWatchlisted(r),
          setWatch: (on) => {
            const targets = pickedRows.filter((r) => !!isWatchlisted(r) !== on);
            if (on && pins.length + targets.length > watchlistLimit) {
              onWatchlistFull?.();
              return;
            }
            const at = {
              origin: on ? "forecast-manager.batch-watch" : "forecast-manager.batch-unwatch",
              batch: targets.length,
            };
            targets.forEach((r) => onTogglePin(r.id, at));
            toast(
              on
                ? `Added ${targets.length} to your watchlist`
                : `Removed ${targets.length} from your watchlist`,
              { kind: "success", ms: 2500 },
            );
            clearPicked();
          },
          // The confirmation lists them; a cancel keeps the selection.
          remove:
            typeof onDelete === "function"
              ? () => onDelete(pickedRows.filter((r) => !isReadOnlyRow(r)))
              : null,
          clear: clearPicked,
        }
      : null;
  const setWatched = (id, on) => {
    const f = byId.get(id);
    if (f && !!isWatchlisted(f) !== on) onTogglePin(id);
  };
  const stackById = (sid) => stacking?.stacks.find((st) => st.id === sid) ?? null;
  const stackOfCard = (id) => stacking?.stacks.find((st) => st.cardIds.includes(id)) ?? null;
  const stackWatched = (st) => st.cardIds.some((c) => byId.get(c) && isWatchlisted(byId.get(c)));
  // A move that would watch a card the full watchlist can't take does nothing, and says so —
  // otherwise the card would land among watched cards without being watched.
  const roomToWatch = (id, on) => {
    const f = byId.get(id);
    if (!on || !f || isWatchlisted(f) || pins.length < watchlistLimit) return true;
    onWatchlistFull?.();
    return false;
  };
  const handlers = stacking && {
    onMove: (id, { list, beforeId, afterId, stackId }) => {
      if (stackId) {
        const st = stackById(stackId);
        if (!st) return;
        if (list === "watchlist") {
          // All or nothing: a stack the watchlist cannot take whole stays where it was.
          const toWatch = st.cardIds.filter((c) => byId.get(c) && !isWatchlisted(byId.get(c)));
          if (pins.length + toWatch.length > watchlistLimit) {
            onWatchlistFull?.();
            return;
          }
        }
        st.cardIds.forEach((c) => setWatched(c, list === "watchlist"));
        // All of its cards move together, so whichever leads it once watch states settle is
        // already at the drop point.
        onMoveInOrder?.(
          st.cardIds.filter((c) => byId.has(c)),
          beforeId,
          afterId,
          { stackId: st.id },
        );
        return;
      }
      if (list.startsWith("stack:")) {
        const st = stackById(list.slice("stack:".length));
        if (!st || !roomToWatch(id, stackWatched(st))) return;
        // The watch state follows only a move that happened (a full stack refuses the card).
        const watched = stackWatched(st);
        if (stacking.onMoveIntoStack(id, st.id, beforeId)) setWatched(id, watched);
        return;
      }
      if (!roomToWatch(id, list === "watchlist")) return;
      if (stackOfCard(id)) stacking.onUnstack(id);
      setWatched(id, list === "watchlist");
      onMoveInOrder?.(id, beforeId, afterId);
    },
    onStack: (id, targetId) => {
      const target = byId.get(targetId);
      if (target && !roomToWatch(id, !!isWatchlisted(target))) return;
      const existing = stackOfCard(targetId);
      const ids = existing ? [...existing.cardIds, id] : [targetId, id];
      const questions = ids.map((c) => byId.get(c)?.question).filter(Boolean);
      if (stacking.onStack(id, targetId, questions) && target) {
        setWatched(id, !!isWatchlisted(target));
      }
    },
  };
  const renderItems = (items, section) =>
    items.map((it) => {
      const row = section === "watchlist" ? watchRow : recentRow;
      if (it.kind === "card") {
        if (!stacking || searching) return row(it.f);
        // A plain card: drags on the board, and clicking it closes any open stack.
        return row(it.f, {
          dragProps: boardRowProps(it.f.id, section, handlers),
          onCardClick: (f, e) => {
            stacking.onCollapseAll();
            onSelect(f, e);
          },
        });
      }
      // A stack's cards each render with their OWN section's row, so a watched card in a
      // Watchlist stack keeps its watch line and an unwatched one keeps its slim row.
      const rowFor = (f, extra) => (isWatchlisted(f) ? watchRow : recentRow)(f, extra);
      return (
        <StackGroup
          key={it.stack.id}
          item={it}
          section={section}
          stacking={stacking}
          handlers={handlers}
          renderRow={rowFor}
          leadActive={active?.id === it.lead.id}
        />
      );
    });
  if (pins.length === 0 && recent.length === 0 && !sharedError && !sharedHasMore) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          padding: 32,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: C.rowInactive,
            border: `1px solid ${C.border}`,
            color: C.grayL,
          }}
        >
          <IconForecasts width={20} height={20} />
        </div>
        <div style={{ fontSize: 13, color: C.grayL, lineHeight: 1.5, maxWidth: 240 }}>
          No forecasts yet. Ask Sayer a question to start.
        </div>
      </div>
    );
  }
  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "12px 12px 24px" }}>
      <style>{CARD_CSS}</style>
      {pickedRows.length > 0 && (
        <div className="sy-fpicked" {...surface("forecast-manager.batch")}>
          <span>
            <span className="sy-num">{pickedRows.length}</span> selected
          </span>
          <span className="sy-fpicked__hint">
            {pickedRows.length > 1 ? "Act on all from any card's menu" : "Shift-click more cards"}
          </span>
          <Button
            size="sm"
            variant="ghost"
            {...ui("forecast-manager.batch-clear")}
            onClick={clearPicked}
          >
            Clear
          </Button>
        </div>
      )}
      {searching && pinsShown.length === 0 && recentShown.length === 0 ? (
        <div
          data-forecast-search-empty=""
          style={{ color: C.gray, fontSize: 13, lineHeight: 1.5, padding: "16px 4px" }}
        >
          No forecast titles match &ldquo;{filterQuery.trim()}&rdquo;.
        </div>
      ) : searching ? (
        <>
          {pinsShown.length > 0 && (
            <>
              <div className="sy-fsection" data-first="">
                <h3>Watchlist</h3>
                <span className="sy-num">{pinsShown.length}</span>
              </div>
              {renderItems(sections.watchlist, "watchlist")}
            </>
          )}
          {recentShown.length > 0 && (
            <>
              <div className="sy-fsection" data-first={pinsShown.length ? undefined : ""}>
                <h3>Recent</h3>
                <span className="sy-num">{recentShown.length}</span>
              </div>
              {renderItems(sections.recent, "recent")}
            </>
          )}
        </>
      ) : (
        <>
          <div className="sy-fsection" data-first="">
            <h3>Watchlist</h3>
            {pins.length > 0 && <span className="sy-num">{pins.length}</span>}
          </div>
          {pins.length === 0 ? (
            <div
              style={{
                color: C.gray,
                fontSize: 12,
                lineHeight: 1.5,
                padding: "12px 14px",
                marginBottom: 6,
                border: `1px dashed ${C.border}`,
                borderRadius: 12,
              }}
            >
              Keep the forecasts you care about at the top: choose Add to watchlist from a
              card&apos;s menu.
            </div>
          ) : (
            renderItems(sections.watchlist, "watchlist")
          )}
          <div className="sy-fsection" data-board-boundary="recent">
            <h3>Recent</h3>
            {recent.length > 0 && <span className="sy-num">{recent.length}</span>}
          </div>
          {renderItems(sections.recent, "recent")}
        </>
      )}
      {/* Called directly, not `<SharedLaneTail .../>` — a plain stateless function, so the
        call and the element produce identical output, and the call keeps its content visible
        to this file's "walk the un-invoked tree" tests. */}
      {SharedLaneTail({
        loading: sharedLoading,
        hasMore: sharedHasMore,
        error: sharedError,
        loadMore: onLoadMoreShared,
      })}
    </div>
  );
}
